import React, { useCallback, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';

import CustomLoading from '../../../util_components/custom_loading';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import DataTable from '../shared/data_table';
import TeacherSlugLine from '../shared/teacher_slug_line';
import LoadFailed from '../shared/load_failed';
import { last_page_offset } from '../shared/paging';
import StatusPill from '../shared/status_pill';
import { toggle_sort } from '../shared/use_sortable';
import IssueActionModal from './issue_action_modal';
import SessionCell from './session_cell';
import MonthPassModal from '../penalties/month_pass_modal';
import { KIND, error_view } from '../penalties/error_handlers';
import { MONTH_PASS_ACTION } from '../penalties/params';
import { month_pass_payload } from '../penalties/requests';
import {
  fetch_issues_csv,
  fetch_needs_attention_issues,
  post_issue_action,
  post_penalty_month_pass
} from '../api';
import { issues_file_name } from '../reports/adapters';
import { error_message, fmt_count, is_permission_error, month_label } from '../format';
import {
  ISSUES_LOAD_FAILED_TEXT,
  ISSUE_ACTION_DROPPED_TEXT,
  close_failure_kind,
  issue_action_view,
  issues_empty_text,
  issues_view
} from './adapters';
import {
  ACTION_ACKNOWLEDGE,
  ACTION_RESOLVE,
  DEFAULT_DIR,
  DEFAULT_SORT,
  DECIDED_STATUS_FILTER,
  DECIDED_STATUS_LABEL,
  ISSUE_STATUSES,
  TIER_LABELS,
  applicable_issue_ids,
  issue_action_payload,
  issues_params,
  offers_month_pass_removal,
  refreshed_issue_rows
} from './params';

/*
 * the issue list of one tier: the Ops label, the suggested action, the raw engine text as detail,
 * and acknowledge / resolve with a mandatory reason. Drilling from a tier row lands here.
 */
const ISSUES_CSV_FAILED_TEXT = 'Could not download the issues CSV. Try again.';
const ISSUES_CSV_EMPTY_TEXT = 'The issues CSV came back empty. Try again.';
const PAGE_SIZE = 100;

// tier_amount (the default) is not listed: CustomSingleSelect always renders its own leading
// "blank value" option for defaultOption, so listing it too would show it twice.
const SORT_OPTIONS = [
  { value: 'amount', label: '₹ at stake' },
  { value: 'first_seen', label: 'First found by system' },
  { value: 'last_seen', label: 'Last found by system' },
  { value: 'teacher_name', label: 'Teacher name' }
];

const STATUS_OPTIONS = ISSUE_STATUSES.map((s) => ({
  value: s,
  label: s.charAt(0) + s.slice(1).toLowerCase().replace('_', '-')
})).concat([{ value: DECIDED_STATUS_FILTER, label: DECIDED_STATUS_LABEL }]);

// initial_status: "See all" opens the list on Ops decisions (tier empty = every tier). on_changed:
// an action landed, so the shell's badges must be re-read.
const IssueTable = ({
  pay_month,
  tier,
  teacher_uuid,
  initial_status = '',
  on_error,
  on_back,
  on_changed
}) => {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(issues_view(null));
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState(initial_status);
  // {key, dir}, API-driven (server-paginated list): a header click or the Sort select both land here
  // and re-fetch, unlike shared/use_sortable's client-side sort_rows.
  const [sort, setSort] = useState({ key: DEFAULT_SORT, dir: DEFAULT_DIR });
  const [selected, setSelected] = useState([]);
  const [action, setAction] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [action_result, setActionResult] = useState(null);
  const [load_failed, setLoadFailed] = useState(false);
  const [action_error, setActionError] = useState('');
  const [downloading, setDownloading] = useState(false);
  // a CSV failure shows beside its button, not in the shell toast.
  const [csv_error, setCsvError] = useState('');
  // the rows of an action the connection dropped; the dialog keeps them for the retry.
  const [dropped_rows, setDroppedRows] = useState(null);
  // Counts drops, so a repeat drop with the same text is announced and focused again.
  const [drop_attempt, setDropAttempt] = useState(0);
  // bumped when the dialog closes, so no answer lands in a later dialog.
  const action_run = useRef(0);
  // the row whose Free Pass Day is being removed, and that write's state.
  const [month_pass_row, setMonthPassRow] = useState(null);
  const [month_pass_saving, setMonthPassSaving] = useState(false);
  const [month_pass_error, setMonthPassError] = useState(null);
  // the bulk-bar opener unmounts once the action clears the selection; focus returns here.
  const heading_ref = useRef(null);
  const focus_heading = () => {
    if (heading_ref.current) heading_ref.current.focus({ preventScroll: true });
  };

  // Only the latest request may render — a slow tier-A response must never land under tier B.
  const request_seq = useRef(0);

  const load = useCallback(
    (next_offset, next_status, next_sort) => {
      request_seq.current += 1;
      const seq = request_seq.current;
      setLoading(true);
      setLoadFailed(false);
      setSelected([]);
      fetch_needs_attention_issues(
        issues_params(
          {
            pay_month,
            tier,
            teacher_uuid,
            status: next_status,
            sort: next_sort.key,
            dir: next_sort.dir
          },
          PAGE_SIZE,
          next_offset
        )
      )
        .then((data) => {
          if (seq !== request_seq.current) return;
          const next = issues_view(data);
          const back = last_page_offset({
            offset: next_offset,
            row_count: next.rows.length,
            total_count: next.total_count,
            page_size: PAGE_SIZE
          });
          if (back !== null) {
            setOffset(back);
            load(back, next_status, next_sort);
            return;
          }
          setView(next);
          setLoading(false);
        })
        .catch((e) => {
          if (seq !== request_seq.current) return;
          on_error(e, 'Failed to load the issues for this tier.');
          setView(issues_view(null));
          setLoadFailed(true);
          setLoading(false);
        });
    },
    [pay_month, tier, teacher_uuid, on_error]
  );

  useEffect(() => {
    setOffset(0);
    setCsvError('');
    load(0, status, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pay_month, tier, teacher_uuid]);

  const go_page = (next_offset) => {
    setOffset(next_offset);
    load(next_offset, status, sort);
  };

  // Shared by the Sort select and a table header click, so both stay in the same {key, dir} state.
  const apply_sort = (next) => {
    setSort(next);
    setOffset(0);
    load(0, status, next);
  };

  // the to-be-reviewed CSV is this list under its current filters and sort, every page.
  const download_csv = () => {
    if (downloading) return;
    setDownloading(true);
    setCsvError('');
    fetch_issues_csv({ pay_month, tier, teacher_uuid, status, sort: sort.key, dir: sort.dir })
      .then(({ csv, file_name }) => {
        setDownloading(false);
        if (!csv) {
          setCsvError(ISSUES_CSV_EMPTY_TEXT);
          return;
        }
        saveAs(
          new Blob([csv], { type: 'text/csv;charset=utf-8' }),
          file_name || issues_file_name(pay_month)
        );
      })
      .catch((e) => {
        setDownloading(false);
        // A lost grant is the shell's no-permission page; anything else stays on this button.
        if (is_permission_error(e)) on_error(e, ISSUES_CSV_FAILED_TEXT);
        else setCsvError(ISSUES_CSV_FAILED_TEXT);
      });
  };

  const toggle = (id) =>
    setSelected((s) => (s.indexOf(id) === -1 ? s.concat([id]) : s.filter((x) => x !== id)));

  const dialog_rows = dropped_rows
    ? refreshed_issue_rows(dropped_rows, view.rows)
    : view.rows.filter((r) => selected.indexOf(r.id) !== -1);

  const submit_action = (reason) => {
    const sent_rows = dialog_rows;
    const ids = applicable_issue_ids(
      sent_rows,
      sent_rows.map((r) => r.id),
      action
    );
    if (!ids.length) return;
    const run = action_run.current;
    setSubmitting(true);
    setActionError('');
    post_issue_action(issue_action_payload(ids, action, reason))
      .then((data) => {
        if (run !== action_run.current) return;
        setDroppedRows(null);
        setActionResult(issue_action_view(data));
        setSubmitting(false);
        load(offset, status, sort);
        if (on_changed) on_changed();
      })
      .catch((e) => {
        if (run !== action_run.current) return;
        setSubmitting(false);
        const kind = close_failure_kind(e);
        if (kind === 'reason_too_long') {
          setActionError(error_message(e, 'The reason is too long.'));
          return;
        }
        if (kind === 'dropped') {
          // in the dialog, not the 5xx body: that would hide that the action may have landed.
          setDroppedRows(sent_rows);
          setDropAttempt((n) => n + 1);
          load(offset, status, sort);
          if (on_changed) on_changed();
          return;
        }
        on_error(e, 'Failed to save the issue action.');
      });
  };

  const close_month_pass = () => {
    setMonthPassRow(null);
    setMonthPassError(null);
  };

  // The removal recomputes the month, which clears this issue; the list and the shell's badges re-read.
  const submit_month_pass = (note) => {
    if (month_pass_saving || !month_pass_row) return;
    setMonthPassSaving(true);
    setMonthPassError(null);
    const row = month_pass_row;
    post_penalty_month_pass(
      month_pass_payload(row.teacher_uuid, row.pay_month, MONTH_PASS_ACTION.REMOVE, note)
    )
      .then(() => {
        close_month_pass();
        load(offset, status, sort);
        if (on_changed) on_changed();
      })
      .catch((e) => {
        const v = error_view(e, { write: true });
        if (v.kind === KIND.PERMISSION) {
          close_month_pass();
          on_error(e, 'Failed to remove the Free Pass Day.');
          return;
        }
        setMonthPassError(v);
      })
      .finally(() => setMonthPassSaving(false));
  };

  const close_modal = () => {
    action_run.current += 1;
    setSubmitting(false);
    setAction('');
    setActionResult(null);
    setActionError('');
    setDroppedRows(null);
  };

  const columns = [
    {
      key: 'select',
      label: '',
      hideInCard: true,
      render: (r) => (
        <input
          type="checkbox"
          checked={selected.indexOf(r.id) !== -1}
          onChange={() => toggle(r.id)}
          aria-label={`Select issue ${r.id}`}
          disabled={!r.selectable}
        />
      )
    },
    {
      key: 'label',
      label: 'Issue',
      sort_key: 'issue',
      render: (r) => (
        <div>
          <div>{r.label}</div>
          <div className="tp-card-field-label">{r.suggested_action}</div>
          {offers_month_pass_removal(r) ? (
            <button
              type="button"
              className="tp-link"
              aria-label={`Remove ${r.teacher_name}'s Free Pass Day for ${month_label(r.pay_month)}`}
              onClick={() => setMonthPassRow(r)}
            >
              Remove Free Pass Day
            </button>
          ) : null}
          <details>
            <summary className="tp-card-field-label">Engine detail</summary>
            <span className="tp-mono">
              {r.issue_code} · {r.flag_reason}
            </span>
          </details>
        </div>
      )
    },
    {
      key: 'teacher_name',
      label: 'Teacher',
      sort_key: 'teacher_name',
      render: (r) => (
        <div>
          <div>{r.teacher_name}</div>
          <TeacherSlugLine slug={r.teacher_slug} />
        </div>
      )
    },
    {
      key: 'session',
      label: 'Session',
      sort_key: 'session_time',
      render: (r) => <SessionCell row={r} />
    },
    {
      key: 'amount_at_stake',
      label: '₹ at stake',
      align: 'right',
      sort_key: 'amount',
      render: (r) => <span className="tp-strong">{r.amount_text}</span>
    },
    {
      key: 'status',
      label: 'Status',
      sort_key: 'status',
      render: (r) => (
        <div>
          <StatusPill tone={r.status_tone}>{r.status_label}</StatusPill>
          {r.resolution_text ? (
            <div className="tp-card-field-label" title={r.resolver_uuid || undefined}>
              {r.resolution_text}
            </div>
          ) : null}
          {r.resolution_note ? (
            <div className="tp-card-field-label">Note: {r.resolution_note}</div>
          ) : null}
        </div>
      )
    },
    {
      key: 'seen',
      label: 'Found by system',
      sort_key: 'first_seen',
      render: (r) => (
        <div className="tp-card-field-label">
          <div>first {r.first_seen}</div>
          <div>last {r.last_seen}</div>
        </div>
      )
    }
  ];

  const page_start = view.total_count === 0 ? 0 : offset + 1;
  const page_end = Math.min(offset + view.rows.length, view.total_count);
  const prev_off = offset <= 0;
  const next_off = offset + PAGE_SIZE >= view.total_count;

  return (
    <div>
      <div className="tp-frow">
        <button type="button" className="tp-btn-ghost" onClick={on_back}>
          ← Back to tiers
        </button>
        <div style={{ width: '200px' }}>
          <CustomSingleSelect
            ariaLabel="Issue status filter"
            label="Status"
            noLabel
            options={STATUS_OPTIONS}
            value={status}
            defaultOption="Open + acknowledged"
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
              load(0, e.target.value, sort);
            }}
          />
        </div>
        <div style={{ width: '220px' }}>
          <CustomSingleSelect
            ariaLabel="Sort issues by"
            label="Sort"
            noLabel
            options={SORT_OPTIONS}
            value={sort.key === DEFAULT_SORT ? '' : sort.key}
            defaultOption="Tier, then ₹ at stake"
            onChange={(e) => apply_sort({ key: e.target.value || DEFAULT_SORT, dir: DEFAULT_DIR })}
          />
        </div>
        <div className="tp-frow-right">
          {/* aria-disabled, not disabled: the pressed button keeps focus while it prepares. */}
          <button
            type="button"
            className="tp-btn-out tp-na-csv"
            onClick={download_csv}
            aria-disabled={downloading ? 'true' : undefined}
          >
            {downloading ? 'Preparing…' : '↓ Download CSV'}
          </button>
          {csv_error ? (
            <div className="tp-rp-card-error" role="alert">
              {csv_error}
            </div>
          ) : null}
        </div>
      </div>

      {selected.length ? (
        <div className="tp-bulkbar">
          <span>{fmt_count(selected.length)} selected</span>
          <button
            type="button"
            className="tp-btn-out"
            onClick={() => setAction(ACTION_ACKNOWLEDGE)}
            disabled={!applicable_issue_ids(view.rows, selected, ACTION_ACKNOWLEDGE).length}
          >
            Acknowledge…
          </button>
          <button
            type="button"
            className="tp-btn"
            onClick={() => setAction(ACTION_RESOLVE)}
            disabled={!applicable_issue_ids(view.rows, selected, ACTION_RESOLVE).length}
          >
            Resolve…
          </button>
          <button type="button" className="tp-btn-ghost" onClick={() => setSelected([])}>
            Clear
          </button>
        </div>
      ) : null}

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-title" tabIndex={-1} ref={heading_ref}>
            {TIER_LABELS[tier] || 'All issues'}{' '}
            {loading || load_failed ? null : (
              <span className="tp-card-hd-sub">
                {view.total_count === 0
                  ? 'No issues'
                  : `Showing ${page_start}–${page_end} of ${view.total_count} · ${view.amount_text} at stake`}
              </span>
            )}
          </span>
        </div>
        {loading ? <CustomLoading /> : null}
        {!loading && load_failed ? (
          <LoadFailed text={ISSUES_LOAD_FAILED_TEXT} on_retry={() => load(offset, status, sort)} />
        ) : null}
        {!loading && !load_failed ? (
          <DataTable
            columns={columns}
            rows={view.rows}
            rowKey={(r) => r.key}
            sort={sort}
            on_sort={(key) => apply_sort(toggle_sort(sort, key))}
            emptyText={issues_empty_text(status)}
            cardTitle={(r) => (
              <span>
                <StatusPill tone={r.tier_tone}>{r.tier_label}</StatusPill> {r.teacher_name}
              </span>
            )}
          />
        ) : null}
      </div>

      {!load_failed && view.total_count > PAGE_SIZE ? (
        <div className="flex items-center gap-3 justify-center mt-3">
          {/* aria-disabled, not disabled: Next on the last page keeps focus. */}
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={prev_off ? undefined : () => go_page(Math.max(0, offset - PAGE_SIZE))}
            aria-disabled={prev_off ? 'true' : undefined}
          >
            Prev
          </button>
          <span className="tp-card-hd-sub">
            {page_start}–{page_end} / {view.total_count}
          </span>
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={next_off ? undefined : () => go_page(offset + PAGE_SIZE)}
            aria-disabled={next_off ? 'true' : undefined}
          >
            Next
          </button>
        </div>
      ) : null}

      {month_pass_row ? (
        <MonthPassModal
          action={MONTH_PASS_ACTION.REMOVE}
          teacher_name={month_pass_row.teacher_name}
          month_text={month_label(month_pass_row.pay_month)}
          saving={month_pass_saving}
          error={month_pass_error}
          on_submit={submit_month_pass}
          on_close={close_month_pass}
          on_focus_lost={focus_heading}
        />
      ) : null}

      <IssueActionModal
        show={!!action}
        action={action}
        rows={dialog_rows}
        submitting={submitting}
        result={action_result}
        dropped_text={dropped_rows ? ISSUE_ACTION_DROPPED_TEXT : ''}
        dropped_attempt={drop_attempt}
        server_error={action_error}
        on_focus_lost={focus_heading}
        on_close={close_modal}
        on_submit={submit_action}
      />
    </div>
  );
};

export default IssueTable;
